import { captureError } from '@smg-real-estate/browser-rum';
import { Category, Country, OfferType } from '@smg-real-estate/hg-rets-schema';
import 'whatwg-fetch';

import {
  OVERTIME_THRESHOLD,
  pricingBundlesCallFailed,
  pricingBundlesCallOvertime,
  pricingBundlesCallRecovered,
  pricingBundlesCallStarted,
  pricingBundlesCallSuccess,
} from '@/helpers/metricUtils';
import { TargetPlatform } from '@/services/envService';
import { BundlePeriod, BundleTier, PricingCustomerSegment } from '@/types';
import { NetworkError } from './NetworkError';

export interface BundleOffer {
  isSpecialDiscount?: boolean;
  crosslisting?: PricingApiBundle[];
}

export interface PricePoint {
  type: PricePointType;
  old_price: number;
  price: number;
  pricePerWeek: number;
}

export interface PricingApiBundle {
  name: BundleTier;
  bundle_id: string; // encoding also variant, canton etc. e.g BUNDLE_EXP-RUB-2662_vB_ZH_BASIC
  price_points: PricePoint[];
  period: BundlePeriod;
  signature?: string;
}

export interface NewConstructionOffer {
  eligible: boolean;
  pricePerWeek: number;
}

export interface PricingParameters {
  country: Country;
  region: string;
  offerType: OfferType;
  objectCategory: Category;
  userId: string;
  campaignId?: string;
  customerSegment: PricingCustomerSegment;
  isTenant: boolean;
  sourcePlatform: TargetPlatform;
  insertionId: string;
  priceGross?: number;
  newConstruction?: NewConstructionOffer;
}

interface GetBundlePricesResponse {
  data: {
    bundles: PricingApiBundle[];
    offer: BundleOffer;
  };
}

export enum PricePointType {
  INITIAL = 'INITIAL',
  RECURRING = 'RECURRING',
}

// eslint-disable-next-line max-statements
export async function getBundlePrices(
  parameters: PricingParameters,
): Promise<GetBundlePricesResponse> {
  let isOvertime = false;

  const timer = setTimeout(() => {
    isOvertime = true;
    pricingBundlesCallOvertime();
  }, OVERTIME_THRESHOLD.PRICING_API);

  pricingBundlesCallStarted();
  const endpoint = getBundlesEndpointUrl(parameters);

  // We use the raw fetch API here instead of the methods in `./fetch.ts`,
  // since we don't need authentication for the Pricing API.
  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: 'GET',
      mode: 'cors',
    });
  } catch {
    const error = new NetworkError('GET', endpoint, 'Failed to fetch');
    captureError(error);
    throw error;
  }

  if (response.ok) {
    if (isOvertime) {
      pricingBundlesCallRecovered();
    }
    clearTimeout(timer);
    pricingBundlesCallSuccess();
    return response.json();
  } else {
    clearTimeout(timer);
    pricingBundlesCallFailed();
    const error = new Error(`Pricing API request returned with error=${response.statusText}.`);
    captureError(error, { endpoint, status: response.status });
    throw error;
  }
}

function getBundlesEndpointUrl(params: PricingParameters) {
  const PRICING_API_BASE_URL = import.meta.env.VUE_APP_PRICING_API_BASE_URL;

  const queryParams = new URLSearchParams({
    country: params.country,
    region: params.region,
    offer_type: params.offerType,
    object_category: params.objectCategory,
    user_id: params.userId,
    insertion_id: params.insertionId,
    customer_segment: params.customerSegment,
    source_platform: params.sourcePlatform,
    is_tenant: params.isTenant.toString(),
  });
  if (params.priceGross) {
    queryParams.append('price_gross', params.priceGross.toString());
  }
  if (params.campaignId) {
    queryParams.append('campaign_id', params.campaignId);
  }

  return `${PRICING_API_BASE_URL}/bundles?${queryParams.toString()}`;
}
